import { useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const SOSButton = () => {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [location, setLocation] = useState(null);

  const handleSOSClick = () => {
    // Get current location
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setShowConfirm(true);
        },
        (error) => {
          console.error("Geolocation error:", error);
          // Use default UF location if geolocation fails
          setLocation({
            lat: 29.6499,
            lng: -82.3486,
          });
          setShowConfirm(true);
        }
      );
    } else {
      // Fallback location
      setLocation({
        lat: 29.6499,
        lng: -82.3486,
      });
      setShowConfirm(true);
    }
  };

  const handleConfirmSOS = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/sos`, {
        lat: location.lat,
        lng: location.lng,
        message: "I need assistance at my current location",
      });
      
      toast.success(
        "Help request sent! Staff has been notified of your location.",
        { duration: 5000 }
      );
      
      // Generate shareable message
      const message = `SOS Request\nLocation: https://maps.google.com/?q=${location.lat},${location.lng}\nMessage: I need assistance`;
      
      // Try to use native share if available
      if (navigator.share) {
        await navigator.share({
          title: "SOS Request",
          text: message,
        });
      } else {
        // Copy to clipboard
        await navigator.clipboard.writeText(message);
        toast.info("Location copied to clipboard");
      }
    } catch (e) {
      console.error("SOS error:", e);
      toast.error("Failed to send SOS. Please call security directly.");
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  return (
    <>
      <button
        className="sos-button w-full animate-pulse-slow touch-target"
        onClick={handleSOSClick}
        disabled={loading}
        aria-label="Emergency help button"
        data-testid="sos-button"
      >
        {loading ? (
          <Loader2 className="animate-spin mr-2 inline" size={24} />
        ) : (
          <AlertTriangle className="mr-2 inline" size={24} />
        )}
        Help Me Now
      </button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={24} />
              Send Help Request?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will send your current GPS location to stadium staff and generate
              an SOS message. Use this if you need immediate assistance.
              {location && (
                <span className="block mt-2 text-xs text-muted-foreground">
                  Your location: {location.lat.toFixed(4)}, {location.lng.toFixed(4)}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSOS}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-sos"
            >
              {loading ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : null}
              Send SOS
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
